import React, { useEffect, useRef, useState } from 'react';
import { Modal as AntModal, Button as AntButton, message, Tabs as AntTabs } from 'antd';
import { getCompanySmtpSettings, sendEmail } from '../services/smtpService';
import { getUserCompanyInfo } from '../services/apiService';

export function QuoteEmailForm({ recipientEmail, quoteName, period, pdfUrl, attachments, onCancel, onSent }) {
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [company, setCompany] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const seIframeRef = useRef(null);
  const [seReady, setSeReady] = useState(false);
  const seGetResolverRef = useRef(null);
  const userEditedRef = useRef(false);
  const templateUpdateRef = useRef(false);

  const getDefaultEmailBody = (url, companyName, title, eventPeriod, attachmentList) => {
    const escapeHtml = (v) => {
      try {
        return String(v || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      } catch {
        return '';
      }
    };

    const getAttachmentFileName = (item) => {
      try {
        if (item?.파일명) return String(item.파일명);
        const p = item?.첨부파일경로 || item?.파일경로 || '';
        if (!p) return '';
        return String(p).split('/').pop() || '';
      } catch {
        return '';
      }
    };

    const resolveAttachmentUrl = (item) => {
      try {
        if (item?.url) return String(item.url);
        const p = item?.첨부파일경로 || item?.파일경로 || '';
        if (!p) return '';
        const path = String(p).replace(/^\/+/, '');
        return `https://kr.object.iwinv.kr/orgin/${path}`;
      } catch {
        return '';
      }
    };

    const buildAttachmentSection = () => {
      try {
        const list = Array.isArray(attachmentList) ? attachmentList : [];
        const items = [];
        const seen = new Set();

        for (const it of list) {
          const href = resolveAttachmentUrl(it);
          if (!href) continue;
          if (seen.has(href)) continue;
          seen.add(href);
          const name = getAttachmentFileName(it) || '첨부파일';
          items.push({ href, name });
        }

        if (!items.length) return '';

        const liHtml = items.map(({ href, name }) => {
          const safeName = escapeHtml(name);
          const safeHref = escapeHtml(href);
          return `<li style="margin:0 0 8px 0;">
            <a href="${safeHref}" target="_blank" style="color:#2563eb; text-decoration:underline;">${safeName}</a>
          </li>`;
        }).join('');

        return `
      <div style="background:#ffffff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin:0 0 16px 0;">
        <div style="margin:0 0 8px 0;"><strong style="color:#374151;">■ 첨부파일 다운로드</strong></div>
        <ul style="margin:0; padding:0 0 0 18px;">${liHtml}</ul>
      </div>`;
      } catch {
        return '';
      }
    };

    const attachmentSection = buildAttachmentSection();
    return `<!DOCTYPE html>
<html>
  <body style="font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif; color:#111827; line-height:1.7; font-size:14px; margin:0; padding:0;">
    <div style="max-width:640px; margin:0 auto; padding:16px;">
      <p style="margin:0 0 12px 0;">안녕하세요.</p>
      <p style="margin:0 0 20px 0;">${companyName || '저희 여행사'}입니다.<br/>요청하신 견적서를 발송해 드립니다.</p>

      <div style="background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin:0 0 16px 0;">
        <div style="margin:0 0 6px 0;"><strong style="color:#374151;">■ 견적명:</strong> ${title || '-'}</div>
        <div><strong style="color:#374151;">■ 여행기간:</strong> ${eventPeriod || '-'}</div>
      </div>

      ${attachmentSection}

      <p style="margin:0 0 12px 0;">아래 버튼을 클릭하여 견적서를 확인하실 수 있습니다.</p>
      <p style="margin:0 0 20px 0;">
        <a href="${url || '#'}" target="_blank" style="display:inline-block; background:#2563eb; color:#ffffff; text-decoration:none; padding:10px 16px; border-radius:8px; font-weight:600;">견적서 열기</a>
      </p>

      ${url ? `<p style="margin:0 0 20px 0; color:#6b7280; font-size:12px;">버튼이 보이지 않으면 아래 링크를 복사하여 브라우저에 붙여넣기 해주세요.<br/><span style="word-break:break-all;">${url}</span></p>` : ''}

      <p style="margin:0 0 12px 0;">문의사항이 있으시면 언제든지 연락 주시기 바랍니다.</p>
      <p style="margin:0 0 24px 0;">감사합니다.</p>
      <p style="margin:0;">${companyName || ''}</p>
    </div>
  </body>
</html>`;
  };

  useEffect(() => {
    (async () => {
      try {
        const smtp = await getCompanySmtpSettings();
        setSmtpSettings(smtp || null);
      } catch (e) {
        console.warn('SMTP 설정 조회 실패:', e);
      }
      try {
        const info = await getUserCompanyInfo();
        setCompany(info || null);
      } catch (e) {
        console.warn('회사 정보 조회 실패:', e);
      }
    })();
  }, []);

  // SmartEditor postMessage bridge: listen for readiness and SE_GET_RESULT
  useEffect(() => {
    const handler = (e) => {
      try {
        const data = e?.data;
        if (!data || typeof data !== 'object') return;
        // Only accept messages from our iframe
        const srcWin = seIframeRef.current?.contentWindow;
        if (srcWin && e?.source && e.source !== srcWin) return;

        if (data.type === 'SE_READY') {
          setSeReady(true);
          return;
        }
        if (data.type === 'SE_GET_RESULT') {
          const resolver = seGetResolverRef.current;
          seGetResolverRef.current = null;
          if (resolver) resolver({ html: String(data.html || '') });
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const pushHtmlToEditor = (html) => {
    try {
      const w = seIframeRef.current?.contentWindow;
      if (!w) return;
      w.postMessage({ type: 'SE_SET', html: String(html || '') }, '*');
    } catch {
      // ignore
    }
  };

  const getEditorHtml = () => {
    return new Promise((resolve) => {
      try {
        const w = seIframeRef.current?.contentWindow;
        if (!w) return resolve({ html: '' });
        seGetResolverRef.current = resolve;
        try { w.postMessage({ type: 'SE_GET' }, '*'); } catch { }

        // safety timeout
        setTimeout(() => {
          if (seGetResolverRef.current === resolve) {
            seGetResolverRef.current = null;
            resolve({ html: '' });
          }
        }, 2000);
      } catch {
        resolve({ html: '' });
      }
    });
  };

  const syncBodyFromEditor = async () => {
    try {
      if (!seReady) return;
      const { html } = await getEditorHtml();
      if (typeof html === 'string' && html) {
        userEditedRef.current = true;
        setEmailBody(String(html || ''));
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const defaultRecipient = String(recipientEmail || '').trim();
    const title = quoteName || '견적서';
    setEmailRecipient(defaultRecipient);
    setEmailSubject(`[견적서] ${title}`);

    // Protect user edits: if SmartEditor content diverged from our last-synced emailBody,
    // treat it as user-edited and do not overwrite with template.
    (async () => {
      try {
        if (userEditedRef.current) return;

        if (seReady) {
          const { html } = await getEditorHtml();
          const editorHtml = String(html || '');
          if (editorHtml && editorHtml !== String(emailBody || '')) {
            userEditedRef.current = true;
            return;
          }
        }

        templateUpdateRef.current = true;
        setEmailBody(getDefaultEmailBody(pdfUrl, company?.업체명, title, period, attachments));
      } catch {
        // ignore
      }
    })();
  }, [recipientEmail, quoteName, period, pdfUrl, attachments, company?.업체명, seReady, emailBody]);

  // When editor is ready, or when template re-initializes the body, push HTML into SmartEditor
  useEffect(() => {
    if (!seReady) return;
    if (!templateUpdateRef.current) return;
    templateUpdateRef.current = false;
    pushHtmlToEditor(emailBody || '');
  }, [seReady, emailBody]);

  const handleSend = async () => {
    if (!emailRecipient?.trim()) { message.error('수신자 이메일을 입력해주세요.'); return; }
    if (!emailSubject?.trim()) { message.error('메일 제목을 입력해주세요.'); return; }
    if (!smtpSettings) { message.error('SMTP 설정이 없습니다.'); return; }
    try {
      setSendingEmail(true);
      const hide = message.loading('이메일 발송 중...', 0);
      const recipients = emailRecipient.split(/[,;]/).map(e => e.trim()).filter(Boolean);

      let finalBody = emailBody;
      try {
        if (seReady) {
          const { html } = await getEditorHtml();
          if (typeof html === 'string' && html.trim()) {
            finalBody = String(html || '');
            userEditedRef.current = true;
            setEmailBody(finalBody);
          }
        }
      } catch {
        // ignore
      }
      await sendEmail({
        recipients,
        subject: emailSubject,
        body: finalBody,
        smtpServer: smtpSettings?.서버주소 || '',
        smtpPort: smtpSettings?.포트 || 465,
        smtpUsername: smtpSettings?.메일주소 || '',
        smtpPassword: smtpSettings?.비밀번호 || '',
      });
      hide();
      message.success('이메일이 성공적으로 발송되었습니다.');
      if (typeof onSent === 'function') onSent();
    } catch (e) {
      console.error('견적 이메일 발송 실패:', e);
      message.error(e?.message || '이메일 발송에 실패했습니다.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="space-y-4 pt-2">
        {!smtpSettings && (
          <div className="p-3 rounded-lg text-sm bg-amber-50 text-amber-700 border border-amber-200">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              SMTP 설정이 필요합니다. 설정 &gt; 발신메일 관리에서 설정해주세요.
            </div>
          </div>
        )}

      

        <div className="space-y-2">
          <label className="block text-[13px] font-semibold text-gray-600">수신자 이메일 <span className="text-red-500">*</span></label>
          <input
            className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm"
            value={emailRecipient}
            onChange={(e) => setEmailRecipient(e.target.value)}
            placeholder="example@email.com (여러 개는 쉼표/세미콜론 구분)"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-[13px] font-semibold text-gray-600">메일 제목 <span className="text-red-500">*</span></label>
          <input
            className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="메일 제목을 입력하세요"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-[13px] font-semibold text-gray-600">메일 본문</label>
          <div className="relative border border-gray-200 rounded-lg overflow-hidden" style={{ height: 420 }}>
            <iframe
              ref={seIframeRef}
              title="SmartEditor"
              src="/smarteditor/SmartEditor4.html"
              className="w-full h-full"
              style={{ border: 0 }}
              onLoad={() => {
                setSeReady(false);
              }}
            />
            {!seReady && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 bg-gray-50/80">
                에디터 로딩중...
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <AntButton onClick={onCancel} size="large">취소</AntButton>
          <AntButton type="primary" onClick={handleSend} loading={sendingEmail} disabled={!smtpSettings} size="large">발송</AntButton>
        </div>
    </div>
  );
}

export default function QuoteEmailModal({ open, onClose, recipientEmail, quoteName, period, pdfUrl, attachments }) {
  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div className="flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          견적서 이메일 발송
        </div>
      }
      width={640}
      destroyOnClose
      maskClosable={false}
    >
      <QuoteEmailForm
        recipientEmail={recipientEmail}
        quoteName={quoteName}
        period={period}
        pdfUrl={pdfUrl}
        attachments={attachments}
        onCancel={onClose}
        onSent={onClose}
      />
    </AntModal>
  );
}
